import { Box, Button, DialogBody, HStack, Text, VStack } from '@chakra-ui/react';
import { SearchFilterCategorySection } from 'components/Search/SearchFilterCategorySection';
import {
  type SearchFilterCategoryDef,
  type SearchFilterSelectionContext,
  SEARCH_FILTER_CATEGORIES,
  buildFacetOptions,
} from 'components/Search/searchFilterCategories';
import { useEffect, useState } from 'react';
import { ModalSearchFilterVariantProps, SharedModalProps } from 'types/Modal';
import { CustomModal } from '../CustomModal';

type Props = SharedModalProps & ModalSearchFilterVariantProps;

/**
 * Filter sheet with deferred apply: edits are held in local draft until „Filter anwenden“.
 * Closing via X or backdrop discards the draft and leaves parent selection unchanged.
 * („Alle zurücksetzen“ clears only the draft; apply is still required to persist.)
 */
export const SearchFilterModal = ({
  isShown,
  handleClose,
  selectedCeTags,
  selectedKbTags,
  setSelectedCeTags,
  setSelectedKbTags,
}: Props): JSX.Element => {
  const [draftCeTags, setDraftCeTags] = useState<string[]>([]);
  const [draftKbTags, setDraftKbTags] = useState<string[]>([]);

  useEffect(() => {
    if (!isShown) return;
    setDraftCeTags([...selectedCeTags]);
    setDraftKbTags([...selectedKbTags]);
  }, [isShown, selectedCeTags, selectedKbTags]);

  const draftCtx: SearchFilterSelectionContext = {
    selectedCeTags: draftCeTags,
    selectedKbTags: draftKbTags,
    setSelectedCeTags: setDraftCeTags,
    setSelectedKbTags: setDraftKbTags,
  };

  const toggleDraftValue = (category: SearchFilterCategoryDef, value: string): void => {
    const current = category.getSelected(draftCtx);
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    category.setSelected(draftCtx, next);
  };

  const handleApply = (): void => {
    setSelectedCeTags(draftCeTags);
    setSelectedKbTags(draftKbTags);
    handleClose();
  };

  const handleResetDraft = (): void => {
    setDraftCeTags([]);
    setDraftKbTags([]);
  };

  const handleResetOrApplySectionDraft = (category: SearchFilterCategoryDef): void => {
    const current = category.getSelected(draftCtx);
    const shouldReset = current.length > 0;
    const allSectionOptions = category.facetCatalog.map((option) => option.value);
    const next = shouldReset ? [] : allSectionOptions;
    category.setSelected(draftCtx, next);
  };

  const sections = SEARCH_FILTER_CATEGORIES.map((category) => ({
    category,
    options: buildFacetOptions(category),
  })).filter((s) => s.options.length > 0);

  return (
    <CustomModal isOpen={isShown} onClose={handleClose} placement="center" size="md" title="Filter">
      <DialogBody p={{ base: '16px 20px', md: '20px 24px' }} display="flex" flexDirection="column">
        <Text srOnly id="search-filter-modal-description">
          Wähle Curriculum-Einheiten und Kompetenzbereiche. Änderungen werden erst nach „Filter
          anwenden“ übernommen.
        </Text>
        <VStack align="stretch" gap="0" flex="1" minH="0">
          {sections.map(({ category, options }, index) => (
            <Box key={category.id}>
              {index > 0 ? (
                <Box borderTopWidth="1px" borderColor="divider.default" my="4" aria-hidden />
              ) : null}
              <SearchFilterCategorySection
                title={category.label}
                options={options}
                selectedValues={category.getSelected(draftCtx)}
                onToggleValue={(value) => toggleDraftValue(category, value)}
                onResetOrApplyAll={() => handleResetOrApplySectionDraft(category)}
              />
            </Box>
          ))}
        </VStack>
      </DialogBody>

      <Box
        borderTopWidth="1px"
        borderColor="divider.default"
        px={{ base: '20px', md: '24px' }}
        py="4"
      >
        <HStack gap="3" justify="space-between" flexWrap="wrap">
          <Button variant="tertiary" onClick={handleResetDraft} order={{ base: 1, md: 0 }}>
            Alle zurücksetzen
          </Button>
          <Button variant="primary" onClick={handleApply} order={{ base: 0, md: 1 }}>
            Filter anwenden
          </Button>
        </HStack>
      </Box>
    </CustomModal>
  );
};
